import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CLOUDS from "vanta/dist/vanta.clouds.min"; // Correct import for CLOUDS
import * as THREE from "three"; // Import Three.js
import "./Home.css";
import Layout from "./Layout";
import About from "./About";
import Contact from "./Contact";
import AnimatedText from "./AnimatedText";
import NavigationBar from "./NavigationBar";
import Intro from "./pages/Intro";
import Stickysocialbar from "./Stickysocialbar";

const Home = () => {
  // Create refs for the sections to scroll to
  const aboutRef = useRef(null);
  const contactRef = useRef(null);
  const homeRef = useRef(null);
  const vantaRef = useRef(null); // Ref for Vanta.js background

  // Parallax effect for profile image
  const { scrollY } = useScroll();
  const yParallax = useTransform(scrollY, [0, 300], [0, 50]);

  // Animation variants for skills
  const skillVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // InView hook for section animations
  const [aboutInViewRef, aboutInView] = useInView({ triggerOnce: true });
  const [contactInViewRef, contactInView] = useInView({ triggerOnce: true });

  // Initialize Vanta.js effect
  useEffect(() => {
    if (!vantaRef.current) return;

    const vantaEffect = CLOUDS({
      el: vantaRef.current,
      THREE: THREE, // Pass Three.js to Vanta
      mouseControls: true,
      touchControls: true,
      gyroControls: false,
      minHeight: 200.0,
      minWidth: 200.0,
    });

    // Cleanup Vanta effect on component unmount
    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, []);

  // Scroll functions
  const scrollToAbout = () => {
    aboutRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    contactRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToHome = () => {
    homeRef.current.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Stickysocialbar />
      <NavigationBar
        scrollToAbout={scrollToAbout}
        scrollToContact={scrollToContact}
        scrolltoHome={scrollToHome}
      />
      <div ref={homeRef}>
        {/* Vanta.js Background Container */}
        <div
          ref={vantaRef}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            zIndex: -1, // Ensure it stays behind content
          }}
        />
        <motion.div
          className="bg-transparent text-gray-100 relative" // Transparent to show Vanta background
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="flex flex-col md:flex-row items-center h-screen">
            <motion.div
              className="w-full md:w-1/2 flex flex-col justify-center"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="md:ml-5 text-center ml-8">
                <h1
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                  className="text-4xl md:text-5xl text-teal-300 font-bold mb-6 text-shadow-lg"
                >
                  Hello, This is Susan
                </h1>
                <div>
                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      padding: "10px",
                    }}
                    className="text-gray-200 mb-1 text-xl md:text-2xl p-3"
                  >
                    A passionate developer and security enthusiast.
                  </p>
                </div>
              </div>
              <motion.div
                className="flex flex-col items-center text-gray-200 text-3xl mb-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <h2 className="text-center text-teal-400 font-bold">Skills</h2>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {["html", "css", "react", "js", "pentest"].map((skill) => (
                    <motion.img
                      key={skill}
                      className="w-24 h-24 hover:scale-110 transition-transform duration-300"
                      src={`/images/${skill}.png`}
                      alt={skill}
                      variants={skillVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover={{
                        rotate: 10,
                        scale: 1.1,
                        boxShadow: "0 0 15px rgba(0, 255, 255, 0.5)",
                        transition: { duration: 0.3 },
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side (Image) */}
            <motion.div
              className="w-full md:w-1/2 flex justify-center mb-8 p-7"
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <motion.img
                style={{ y: yParallax }}
                className="w-80 h-80 rounded-full shadow-lg border-4 border-teal-500 object-cover hover:scale-105 transition-transform duration-300"
                src="/images/profile1.jpg"
                alt="susan-profile"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
      <Intro />
      <div ref={aboutRef}>
        <motion.div
          ref={aboutInViewRef}
          initial={{ opacity: 0, y: 50 }}
          animate={aboutInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <About />
        </motion.div>
      </div>
      <div ref={contactRef}>
        <motion.div
          ref={contactInViewRef}
          initial={{ opacity: 0, y: 50 }}
          animate={contactInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Contact />
        </motion.div>
      </div>
    </>
  );
};

export default Home;
